import type {
  Locale,
  PinSetting,
  PrivacySettings,
  TranslationGlobalSettings,
  UserSettings,
} from './types';

/** 일일 번역 문자 한도 기본값 (PLAN 5-7장 과금 보호) */
export const DEFAULT_DAILY_CHAR_LIMIT = 5000;

/**
 * 번역 전역 설정 기본값 (PLAN 5-7-2장).
 * 기본은 꺼짐 + 자동 감지 — 켜는 순간 과금이 시작되므로 명시적 선택을 요구한다.
 */
export function createDefaultTranslationSettings(locale: Locale): TranslationGlobalSettings {
  return {
    globalMode: 'off',
    targetLang: locale,
    autoDetectTarget: true,
    defaultPerRoomMode: 'inherit',
    dailyCharLimit: DEFAULT_DAILY_CHAR_LIMIT,
  };
}

/** 인앱 배너 기본 노출 시간 (PLAN 5-8-7장) */
export const DEFAULT_BANNER_MS = 4_000;

/**
 * `privacy/{uid}` 초기값 (PLAN 5-10장 경량 프라이빗 모드).
 *
 * 문서가 아직 없을 때의 해석이 곧 기본값이라, 클라이언트와 Functions가
 * **같은 함수**로 기본값을 계산해야 한다. (한쪽만 다르면 푸시 미리보기 유무가 어긋난다)
 */
export function createDefaultPrivacySettings(): PrivacySettings {
  return {
    notificationsOff: false,
    readReceipts: true,
    typingIndicator: true,
    lastSeen: 'all',
    inAppBanners: true,
    inAppBannerDurationMs: DEFAULT_BANNER_MS,
    previewOnLock: true,
    autoDownloadOnCellular: false,
  };
}

/**
 * 없는 필드를 기본값으로 채우고, 알려진 필드만 남긴다.
 *
 * privacy 문서는 부분 쓰기라 필드가 없을 수 있어(구버전 문서, 부분 업데이트) 항상 이 경로를 탄다.
 * 스프레드를 그대로 쓰지 않는 이유: Rules가 본인 문서 쓰기를 허용하므로 알 수 없는 키가
 * 섞여 들어올 수 있고, 그것을 그대로 신뢰하면 나중에 "이 필드가 뭔지"를 알 수 없게 된다.
 */
export function resolvePrivacySettings(
  partial: Partial<PrivacySettings> | undefined,
): PrivacySettings {
  const source = partial ?? {};
  return {
    notificationsOff: source.notificationsOff ?? false,
    readReceipts: source.readReceipts ?? true,
    typingIndicator: source.typingIndicator ?? true,
    lastSeen: source.lastSeen ?? 'all',
    inAppBanners: source.inAppBanners ?? true,
    inAppBannerDurationMs: source.inAppBannerDurationMs ?? DEFAULT_BANNER_MS,
    previewOnLock: source.previewOnLock ?? true,
    autoDownloadOnCellular: source.autoDownloadOnCellular ?? false,
  };
}

/**
 * 신규 가입 시 `users/{uid}.settings` 초기값.
 *
 * 온보딩에서 PIN을 필수로 설정하므로 `pin`은 호출자가 만들어 넘긴다
 * (UserSettings.pin은 선택 필드가 아니다).
 */
export function createDefaultUserSettings(locale: Locale, pin: PinSetting): UserSettings {
  return {
    theme: 'system',
    enterToSend: true,
    readReceipts: true,
    typingIndicator: true,
    autoDownloadOnCellular: false,
    doubleTapReaction: '✅',
    translation: createDefaultTranslationSettings(locale),
    pin,
  };
}
